import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import Maybe from "components/__new/common/Maybe";
import useLoginInfo from "hooks/useLoginInfo";
import * as approvalAction from "store/entities/approvals/action";
import { ReactComponent as IconAdd } from "assets/images/icons/project-icons/Plus.svg"
import useInlinePageRouter from "components/__new/general/useInlinePageRouter";
import IconEmptyBox from "assets/images/icons/project-icons/empty-box.png";
import processParams from "utils/processParams";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import Filter from "components/__new/common/Filter";
import SearchBox from "components/__new/common/SearchBox";
import ContentLoading from "components/__new/common/ContentLoading";
import Table from "components/__new/common/Table";
import pluralize from "utils/pluralize";
import Pagination from "components/__new/common/pagination";
import { TableContent } from "components/__new/common/TableContent";
import { renderStatusColor } from "utils/statusColor";
import DropdownNew from "utils/dropdown";
import DeleteWorkflow from "../../../components/__new/approval/workflow/DeleteWorkflow";
import CreateWorkflow from "../../../components/__new/approval/workflow/CreateWorkflow";
import ViewWorkflowDetails from "../../../components/__new/approval/workflow/ViewWorkflowDetails";
import DisableWorkflow from "../../../components/__new/approval/workflow/DisableWorkflow";
import EditWorkflow from "components/__new/approval/workflow/EditWorkflow";
import EnableWorkflow from "components/__new/approval/workflow/EnableWorkflow";

const Index = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const {userInfo} = useLoginInfo();
    const { InlineRouterTab } = useInlinePageRouter();
    const {urlQueryParams} = useUrlQueryParams();
    const { approvalWorkflowsLoading, approvalWorkflows } = useSelector((s) => s.entities.approvals);
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });
    const printComponentRef = useRef();
    const [disableWorkflowOpen, setDisableWorkflowOpen] = useState(false);
    const [enableWorkflowOpen, setEnableWorkflowOpen] = useState(false);
    const [deleteWorkflowOpen, setDeleteWorkflowOpen] = useState(false);
    const [workflow, setWorkflow] = useState(null);
    const [isOpenCreateWorkflow, setIsOpenCreateWorkflow] = useState(false);
    const [isOpenViewWorkflowDetails, setIsOpenViewWorkflowDetails] = useState(false);
    const [isOpenEditWorkflow, setIsOpenEditWorkflow] = useState(false);

    const refresh = () => {
      setWorkflow(null);
      setDisableWorkflowOpen(false);
      setDeleteWorkflowOpen(false);
    }

    const getWorkflows = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "search", "string", "", "search");
        params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
        params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");
        params = processParams(urlQueryParams, params, "request-type", "string", "", "type");
        params = processParams(urlQueryParams, params, "status", "string", "", "status");

        setListParams(params);
        dispatch(approvalAction.getApprovalWorkflows({...params, employerId: userInfo?.employer?._id}))
    }

    useEffect(() => {
        getWorkflows()
        // eslint-disable-next-line
    }, [location]);

    return (
        <div>

            <DynamicHeadTag
                headerText="Approvals"
            />

            <DeleteWorkflow
                isOpen={deleteWorkflowOpen}
                setIsOpen={setDeleteWorkflowOpen}
                workflow={workflow}
                getWorkflows={getWorkflows}
            />

            <DisableWorkflow
                isOpen={disableWorkflowOpen}
                setIsOpen={setDisableWorkflowOpen}
                workflow={workflow}
                getWorkflows={getWorkflows}
            />

            <EnableWorkflow
                isOpen={enableWorkflowOpen}
                setIsOpen={setEnableWorkflowOpen}
                workflow={workflow}
                getWorkflows={getWorkflows}
            />

            <CreateWorkflow
                isOpen={isOpenCreateWorkflow}
                setIsOpen={setIsOpenCreateWorkflow}
                getWorkflows={getWorkflows}
            />

            <ViewWorkflowDetails
                isOpen={isOpenViewWorkflowDetails}
                setIsOpen={setIsOpenViewWorkflowDetails}
                workflow={workflow}
            />

            <EditWorkflow
                isOpen={isOpenEditWorkflow}
                setIsOpen={setIsOpenEditWorkflow}
                workflow={workflow}
                getWorkflows={getWorkflows}
            />

            <div className="w-full lg:w-1/2 mb-5">
                <div className="page-title flex">
                    Approvals
                </div>
            </div>

            <div className="flex items-center">
                <InlineRouterTab
                    items={[
                        {
                            title: "Approval Dashboard",
                            path: "/approvals/dashboard"
                        },
                        {
                            title: "Approval Workflow",
                            path: "/approvals/workflow"
                        }
                    ]}
                />
            </div>

            <div className="mt-6 box">
                <div className="flex items-center justify-start w-max space-x-3">
                    <div className="font-medium">Workflow List</div>
                    <div className="label h-6 text-sm font-normal text-ep-primary-600 bg-[#ecf2f2]">
                        <ContentLoading
                            loading={approvalWorkflowsLoading}
                            data={<>{approvalWorkflows?.data?.totalDocs ?? "-"} {pluralize(approvalWorkflows?.data?.totalDocs, "workflow", "workflows")}</>}
                        />
                    </div>
                </div>

                <div className="mt-6 flex flex-wrap justify-between">
                    <>
                        <div className="max-w-sm flex-grow sm:mr-6">
                            <SearchBox placeholder="Search" name="search" extraUrlParams="page=1" />
                        </div>
                        <div className="flex flex-wrap space-x-3 sm:space-x-3">
                            <div>
                                <Filter filters={[
                                    {
                                      title: "Request Type",
                                      name: "request-type",
                                      dataType: "select",
                                      options: [
                                          {
                                              display: "All",
                                              payload: "",
                                          },
                                          {
                                              display: "Transfer",
                                              payload: "transfer",
                                          },
                                          {
                                              display: "Multiple Transfer",
                                              payload: "multiple transfer",
                                          },
                                        //   {
                                        //     display: "Bulk Transfer",
                                        //     payload: "bulk transfer"
                                        //   },
                                          {
                                            display: "Bill Payment",
                                            payload: "bill payment"
                                          },
                                        //   {
                                        //     display: "Multiple bill payment",
                                        //     payload: "multiple bill payment"
                                        //   }
                                      ],
                                    },
                                    {
                                        title: "Status",
                                        name: "status",
                                        dataType: "select",
                                        options: [
                                            {
                                                display: "All",
                                                payload: "",
                                            },
                                            {
                                                display: "Active",
                                                payload: "active",
                                            },
                                            {
                                                display: "Disable",
                                                payload: "disable",
                                            },
                                        ],
                                    },
                                ]}/>
                            </div>
                            
                            <div>
                              <button type="button" onClick={() => {
                                  setIsOpenCreateWorkflow(true);
                                }} className="btn btn-ep-primary rounded-lg flex items-center justify-center h-full">
                                  <IconAdd width={15} className="mr-2" />
                                  <span>Create New Workflow</span>
                              </button>
                            </div>

                            {/* <div>
                                <DownloadAsCSV
                                    ref={downloadAsCSVRef}
                                    tooltip="Download approval request list"
                                    filename="Workflow-list.csv"
                                    columns={[
                                        { label: "Workflow Title", key: "title" },
                                        { label: "Request Type", key: "type" },
                                        { label: "Status", key: "status" },
                                        { label: "Description", key: "description" },
                                    ]}
                                    click={() => {
                                        dispatch(walletAction.getTransactionsDownload(userInfo.token, {...listParams, page: 1, limit: 10000})).then(() => {
                                            downloadAsCSVRef.current?.downloadData();
                                        });
                                    }}
                                    loading={transactionsDownloadLoading}
                                    response={transactionsDownload?.data?.docs}
                                />
                            </div>

                            <div>
                                <Print component={printComponentRef} />
                            </div> */}
                        </div>
                    </>
                </div>
            </div>

            <div className="bg-white">
                <div ref={printComponentRef}>
                    <div className="mb-4s text-xl hidden print:block">
                        Workflow List
                    </div>

                    <Table classTable="table-hover">
                        <Table.Head>
                            <th>Workflow Title</th>
                            <th>Request Type</th>
                            <th>Description</th>
                            <th>Status</th>
                            <th></th>
                        </Table.Head>
                        <Table.Body>

                          <TableContent
                              loading={approvalWorkflowsLoading}
                              loadingText="Loading approval workflows"
                              data={approvalWorkflows?.data?.data}
                              colspan={6}
                              emptyUI={<>
                                  <div className="py-20 mx-auto text-center text-base">
                                      <div className="flex justify-center">
                                          <img src={IconEmptyBox} alt="Empty" className="h-48" />
                                      </div>
                                      <div className="text-[#101828] font-medium">
                                          No Data Available Yet
                                      </div>
                                      <div className="mt-1 text-[#4A5564]">
                                          Once you create a workflow, it will appear here
                                      </div>
                                  </div>
                              </>}
                          />

                          {approvalWorkflowsLoading === false && approvalWorkflows?.data?.data?.map((workflow, index) => {
                              return (
                                  <tr key={index}>
                                      <td className="capitalize">
                                        {workflow?.title || "N/A"}
                                      </td>
                                      <td className="capitalize">
                                          {workflow?.type || "N/A"}
                                      </td>
                                      <td>
                                          {workflow?.description || "N/A"}
                                      </td>
                                      <td>
                                          <div className={"label capitalize " + renderStatusColor(workflow?.status)}>
                                              {workflow?.status}
                                          </div>
                                      </td>
                                      <td>
                                          <DropdownNew flipSwitch>
                                              <></>
                                              <div className="flex flex-col items-start">
                                                  <button onClick={() => {
                                                      setWorkflow(workflow);
                                                      setIsOpenViewWorkflowDetails(true);
                                                  }} className="dropdown-item pr-8">
                                                      View
                                                  </button>

                                                    <button onClick={() => {
                                                        setWorkflow(workflow);
                                                        setIsOpenEditWorkflow(true);
                                                      }} className="dropdown-item pr-8 w-full">
                                                        Edit
                                                    </button>

                                                  <Maybe condition={workflow?.status?.toLowerCase() === 'active'}>
                                                    <button onClick={() => {
                                                        setWorkflow(workflow);
                                                        setDisableWorkflowOpen(true);
                                                    }} className="dropdown-item pr-8 w-full">
                                                        Disable
                                                    </button>
                                                  </Maybe>

                                                  <Maybe condition={workflow?.status?.toLowerCase() === 'disabled'}>
                                                    <button onClick={() => {
                                                        setWorkflow(workflow);
                                                        setEnableWorkflowOpen(true);
                                                    }} className="dropdown-item pr-8 w-full">
                                                        Enable
                                                    </button>
                                                  </Maybe>

                                                  <button onClick={() => {
                                                      setWorkflow(workflow);
                                                      setDeleteWorkflowOpen(true);
                                                  }} className="dropdown-item pr-8 w-full text-[#dc2626]">
                                                      Delete
                                                  </button>

                                              </div>
                                          </DropdownNew>
                                      </td>
                                  </tr>
                                )
                              })}

                        </Table.Body>
                    </Table>

                    <Maybe condition={approvalWorkflowsLoading === false && approvalWorkflows?.data?.data?.length > 0}>
                        <div className="mt-6">
                            <Pagination data={approvalWorkflows?.data} />
                        </div>
                    </Maybe>
                </div>
            </div>
        </div>
    )
}


export default Index;